import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { motion } from "framer-motion";
import { Capacitor } from "@capacitor/core";
import Ripple from "@/components/Ripple";
import logoFullNavy from "@/assets/logo-full-navy.svg";
import logoFullWhite from "@/assets/logo-full-white.svg";
import logoFullBlack from "@/assets/logo-full-black.svg";

const Signup = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    checkAuth();

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        handleAuthenticatedUser(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleAuthenticatedUser = async (userId: string) => {
    const { data: profile } = await supabase
      .from("worker_profiles")
      .select("psychometric_profile")
      .eq("user_id", userId)
      .maybeSingle();

    if (profile) {
      if (!profile.psychometric_profile) {
        navigate("/onboarding");
      } else {
        navigate("/home");
      }
    }
  };

  const checkAuth = async () => {
    try {
      const {
        data: { session },
        error
      } = await supabase.auth.getSession();

      // Check if it's a network error
      if (error) {
        const isNetworkError = 
          error.message?.includes('fetch') ||
          error.message?.includes('network') ||
          error.message?.includes('Failed to fetch');
        
        if (isNetworkError) {
          // Check for cached session
          const cachedSession = localStorage.getItem('sorin_cached_session');
          if (cachedSession) {
            try {
              const cached = JSON.parse(cachedSession);
              // Session cached less than 24 hours ago
              if (Date.now() - cached.cachedAt < 24 * 60 * 60 * 1000) {
                navigate("/home");
                return;
              }
            } catch (e) {
              console.error('Failed to parse cached session:', e);
            }
          }
          // No valid cache, show offline message but don't error
          console.log('Offline with no cached session');
        } else {
          throw error;
        }
      }

      if (session?.user) {
        await handleAuthenticatedUser(session.user.id);
      }
    } catch (error) {
      console.error("Auth check error:", error);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (authError) throw authError;

      const { data: profile, error: profileError } = await supabase
        .from("worker_profiles")
        .select("*")
        .eq("user_id", authData.user.id)
        .maybeSingle();

      if (profileError) throw profileError;

      if (!profile) {
        await supabase.auth.signOut();
        toast({
          title: "Access Denied",
          description: "Your account has not been registered by an administrator. Please contact your supervisor.",
          variant: "destructive",
        });
        return;
      }

      if (!profile.psychometric_profile) {
        navigate("/onboarding");
      } else {
        navigate("/home");
      }
    } catch (error: any) {
      console.error("Login error:", error);
      toast({
        title: "Login Failed",
        description: error.message || "Invalid email or password",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state while checking existing session
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Ripple mainCircleSize={200} mainCircleOpacity={0.1} numCircles={8} className="z-0" />
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="relative z-10">
          <img src={logoFullBlack} alt="SORIN" className="h-10 dark:hidden" />
          <img src={logoFullWhite} alt="SORIN" className="h-10 hidden dark:block" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col relative overflow-hidden">
      {/* Ripple Background - centered */}
      <Ripple mainCircleSize={280} mainCircleOpacity={0.08} numCircles={10} className="z-0" />

      {/* Top Section - Logo */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className={`relative z-10 flex justify-center pt-24 pb-8 ${isNative ? "safe-area-top" : ""}`}
      >
        <img src={logoFullBlack} alt="SORIN" className="h-14 dark:hidden" />
        <img src={logoFullWhite} alt="SORIN" className="h-14 hidden dark:block" />
      </motion.header>

      {/* Spacer - pushes form to bottom */}
      <div className="flex-1" />

      {/* Bottom Section - Form */}
      <motion.main
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className={`relative z-10 px-6 pb-8 ${isNative ? "safe-area-bottom" : ""}`}
      >
        <div className="w-full max-w-sm mx-auto space-y-6">
          {/* Welcome Text */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-foreground mb-1">Welcome back</h1>
            <p className="text-muted-foreground text-sm">Sign in to continue</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            {/* Email Input - Pill Style */}
            <div className="relative">
              <Input
                id="email"
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-14 text-base rounded-full bg-muted/50 border-border/50 focus:border-primary focus:ring-1 focus:ring-primary/20 px-5 placeholder:text-muted-foreground/60"
                required
                disabled={isLoading}
                autoComplete="email"
              />
            </div>

            {/* Password Input - Pill Style with Toggle */}
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-14 text-base rounded-full bg-muted/50 border-border/50 focus:border-primary focus:ring-1 focus:ring-primary/20 px-5 pr-12 placeholder:text-muted-foreground/60"
                required
                disabled={isLoading}
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground/60 hover:text-muted-foreground transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>

            {/* Sign In Button - Pill Style */}
            <Button
              type="submit"
              disabled={!email || !password || isLoading}
              className="w-full h-14 text-base font-semibold rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20 mt-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          {/* Footer Text */}
          <p className="text-center text-sm text-muted-foreground pt-4">Contact your supervisor if you need access</p>
        </div>
      </motion.main>
    </div>
  );
};

export default Signup;
